<template>
  <PageHeader image="header-7" withMobile :title="$lang('title')" :breadcrumbs="$lang('breadcr')" />

  <!-- Intro -->
  <section class="pt-8 pb-7 bg-blue4">
    <div class="container">
      <div class="row align-items-center">
        <div class="col-lg-7 mb-5 mb-lg-0">
          <h2 class="fw-semibold text-green1 mb-4">
            {{ $lang("section1.title") }}
          </h2>
          <p class="lp-lead text-blue1 fw-semibold mb-4">
            {{ $lang("section1.lead") }}
          </p>
          <p class="mb-5" v-html="$lang('section1.text1')"></p>
          <a href="https://pcn.ie" class="btn btn-blue2 px-4 py-2" target="_blank" rel="noopener">
            {{ $lang("section1.button") }}
          </a>
        </div>
        <div class="col-lg-5">
          <div class="lp-partners">
            <div
              v-for="(partner, index) in $lang('section1.partners')"
              :key="index"
              class="lp-partner"
            >
              <span class="lp-partner-step">{{ index + 1 }}</span>
              <div>
                <p class="lp-partner-name">{{ partner.name }}</p>
                <p class="lp-partner-role">{{ partner.role }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Why it matters -->
  <section class="py-7">
    <div class="container">
      <div class="row">
        <div class="col-lg-10">
          <h3 class="fw-semibold text-blue2 mb-4">{{ $lang("section2.title") }}</h3>
          <p class="mb-4">{{ $lang("section2.text1") }}</p>
          <p class="mb-4">{{ $lang("section2.text2") }}</p>
        </div>
      </div>
      <div class="lp-options">
        <div
          v-for="(option, index) in $lang('section2.options')"
          :key="index"
          class="lp-option"
        >
          <i :class="[option.icon, 'lp-option-icon']"></i>
          <div>
            <p class="lp-option-title">{{ option.title }}</p>
            <p class="mb-0">{{ option.text }}</p>
          </div>
        </div>
      </div>
      <p class="lp-note mt-3 mb-0">{{ $lang("section2.note") }}</p>
    </div>
  </section>

  <!-- Insurance cover -->
  <section class="py-7 bg-blue4">
    <div class="container">
      <div class="row">
        <div class="col-lg-10">
          <h3 class="fw-semibold text-blue2 mb-4">{{ $lang("section3.title") }}</h3>
          <p class="mb-4">{{ $lang("section3.text1") }}</p>
          <p class="mb-5">{{ $lang("section3.text2") }}</p>
          <p class="fw-semibold text-blue1 mb-4">{{ $lang("section3.listTitle") }}</p>
        </div>
      </div>
      <div class="lp-cover">
        <div
          v-for="(item, index) in $lang('section3.items')"
          :key="index"
          class="lp-cover-item"
        >
          <span class="lp-cover-icon"><i :class="item.icon"></i></span>
          <p class="lp-cover-title">{{ item.title }}</p>
        </div>
      </div>
    </div>
  </section>

  <!-- Certificate -->
  <section class="py-7">
    <div class="container">
      <div class="row align-items-center">
        <div class="col-lg-7 mb-5 mb-lg-0">
          <h3 class="fw-semibold text-blue2 mb-4">
            <i class="fa-solid fa-circle-check text-green1 me-2 mr-2"></i>
            {{ $lang("section4.title") }}
          </h3>
          <p class="mb-4">{{ $lang("section4.text1") }}</p>
          <p class="mb-5">{{ $lang("section4.text2") }}</p>
          <blockquote class="lp-quote mb-5">
            {{ $lang("section4.quote") }}
          </blockquote>
          <a
            :href="certificateUrl"
            class="btn btn-blue2 px-4 py-2"
            target="_blank"
            rel="noopener"
          >
            <i class="fa-solid fa-file-shield me-2 mr-2"></i>
            {{ $lang("section4.button") }}
          </a>
        </div>
        <div class="col-lg-5">
          <a :href="certificateUrl" target="_blank" rel="noopener" class="lp-certificate">
            <img :src="certificateUrl" :alt="$lang('section4.imageAlt')" loading="lazy" />
          </a>
        </div>
      </div>
    </div>
  </section>

  <!-- Closing -->
  <section class="pb-9">
    <div class="container">
      <div class="lp-closing">
        <p class="lp-closing-text">{{ $lang("section5.text") }}</p>
        <h3 class="lp-closing-title">{{ $lang("section5.title") }}</h3>
        <button class="btn lp-closing-btn px-5 py-2" @click="goTo('contact_us')">
          {{ $lang("section5.button") }}
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import PageHeader from "@/components/PageHeader";

export default {
  components: {
    PageHeader,
  },
  data() {
    return {
      certificateUrl: "/imgs/learner-protection-certificate.png",
    };
  },
  methods: {
    goTo(routeName) {
      this.$router.push({ name: routeName });
    },
  },
};
</script>

<style>
.lp-lead {
  font-size: 1.3rem;
  line-height: 1.5;
}

/* Partner chain */
.lp-partners {
  background: #fff;
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow);
  padding: 30px;
}

.lp-partner {
  display: flex;
  align-items: center;
  gap: 18px;
  position: relative;
  padding: 14px 0;
}

.lp-partner:not(:last-child)::after {
  content: "";
  position: absolute;
  left: 21px;
  top: 58px;
  height: calc(100% - 44px);
  border-left: 2px dashed var(--blue5);
}

.lp-partner-step {
  flex: 0 0 44px;
  height: 44px;
  border-radius: 50%;
  background: var(--blue2);
  color: #fff;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lp-partner:last-child .lp-partner-step {
  background: var(--green1);
}

.lp-partner-name {
  font-weight: 700;
  font-size: 1.2rem;
  color: var(--blue1);
  margin-bottom: 0;
}

.lp-partner-role {
  margin-bottom: 0;
  color: var(--green1);
}

/* Protection options */
.lp-options {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

.lp-option {
  display: flex;
  align-items: center;
  gap: 20px;
  border: 2px solid var(--blue4);
  border-left: 6px solid var(--green1);
  border-radius: var(--radius-xs);
  padding: 24px;
}

.lp-option-icon {
  font-size: 1.8rem;
  color: var(--blue2);
}

.lp-option-title {
  font-weight: 700;
  font-size: 1.15rem;
  color: var(--blue1);
  margin-bottom: 4px;
}

.lp-note {
  font-size: 0.9rem;
  font-style: italic;
  color: #6c757d;
}

/* Cover grid */
.lp-cover {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}

.lp-cover-item {
  background: #fff;
  border-radius: var(--radius-sm);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  padding: 30px 20px;
  text-align: center;
  transition: transform 0.2s ease;
}

.lp-cover-item:hover {
  transform: translateY(-4px);
}

.lp-cover-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 18px;
  border-radius: 50%;
  background: var(--blue4);
  color: var(--blue2);
  font-size: 1.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lp-cover-title {
  font-weight: 600;
  color: var(--blue1);
  margin-bottom: 0;
}

/* Certificate */
.lp-quote {
  border-left: 4px solid var(--green1);
  padding: 6px 0 6px 20px;
  font-size: 1.2rem;
  font-style: italic;
  color: var(--blue1);
}

.lp-certificate {
  display: block;
  max-width: 380px;
  margin: 0 auto;
  border-radius: var(--radius-xs);
  overflow: hidden;
  box-shadow: var(--shadow);
  transform: rotate(-2deg);
  transition: transform 0.2s ease;
}

.lp-certificate:hover {
  transform: rotate(0deg) scale(1.02);
}

.lp-certificate img {
  display: block;
  width: 100%;
  height: auto;
}

/* Closing banner */
.lp-closing {
  background: linear-gradient(270deg, var(--green1), var(--blue2));
  border-radius: var(--radius-md);
  color: #fff;
  text-align: center;
  padding: 50px 30px;
}

.lp-closing-text {
  max-width: 760px;
  margin: 0 auto 16px;
  font-size: 1.15rem;
}

.lp-closing-title {
  font-weight: 700;
  color: #fff;
  margin-bottom: 28px;
}

.lp-closing-btn {
  background: #fff;
  color: var(--blue2);
  font-weight: 600;
}

.lp-closing-btn:hover {
  background: var(--blue1);
  color: #fff;
}

@media (max-width: 991px) {
  .lp-cover {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 767px) {
  .lp-options,
  .lp-cover {
    grid-template-columns: 1fr;
  }

  .lp-closing {
    padding: 36px 20px;
  }
}
</style>
